import { useEffect } from 'react'
import { useParams } from 'react-router'
import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import useWindowScrollPosition from 'helpers/useWindowScrollPosition'

import Document from 'js/components/Document'
import accessControlFragment from 'js/components/EntityActions/fragments/accessControl'
import { NotFound } from 'js/core/NotFound'
import { groupMenuFieldsFragment } from 'js/group/GroupMenuFields.fragment'
import { ownerGroupVcardFragment } from 'js/lib/fragments/owner'
import { userIntroFragment } from 'js/lib/fragments/UserIntro.fragment'
import { useGlobalState } from 'js/lib/GlobalState/useGlobalState'
import { rowsViewFragment } from 'js/page/Page/rowsFragment'

import Page from './Page/Page'
import Text from './Text/Text'

type Props = {
    guid?: string
    isHome?: boolean
}

const Item = ({ guid: homePageGuid, isHome }: Props) => {
    const { setGlobalState } = useGlobalState()

    const params = useParams()

    const guid = homePageGuid || params.guid
    const urlSlug = params.urlSlug

    const { loading, data } = useQuery<any>(GET_PAGE_VIEW, {
        variables: {
            guid,
            urlSlug,
            groupGuid: params.containerGuid,
        },
        fetchPolicy: 'cache-and-network',
    })

    useEffect(() => {
        if (data?.entity) {
            setGlobalState({ canEditPage: data?.entity?.canEdit })
        }
    }, [data])

    useWindowScrollPosition(guid || urlSlug)

    if (loading) {
        return null
    }
    if (!data?.entity) {
        return <NotFound />
    }

    const { site, entity, viewer } = data
    const { pageType, group } = entity

    return (
        <>
            <Document
                title={!isHome && entity.title}
                containerTitle={group?.name}
            />
            {pageType === 'text' ? (
                <Text entity={entity} />
            ) : (
                <Page site={site} entity={entity} viewer={viewer} />
            )}
        </>
    )
}

export const pageViewSiteFragment = gql`
    fragment PageViewSiteFragment on Site {
        guid
        customTagsAllowed
        tagCategories {
            name
            values
        }
        activityFilter {
            contentTypes {
                key
                value
            }
        }
        searchFilter {
            contentTypes {
                key
                value
            }
        }
        searchDisplayedFilters
        searchTagFilters {
            name
            tagFilterEnabled
            tagFilterType
        }
        searchRelatedSitesEnabled
    }
`

const GET_PAGE_VIEW = gql`
    query PageView($guid: String, $urlSlug: String, $groupGuid: String) {
        site {
            guid
            ...PageViewSiteFragment
        }
        viewer {
            guid
            loggedIn
            user {
                ...UserIntro
            }
            canWriteToContainer(
                containerGuid: $guid
                subtype: "page"
            )
            isSubEditor
        }
        entity(guid: $guid, urlSlug: $urlSlug) {
            guid
            ... on Page {
                pageType
                statusPublished
                canEdit
                canArchiveAndDelete
                preventDeleteAndArchive
                title
                localTitle
                url
                description
                richDescription
                localRichDescription
                isTranslationEnabled
                inputLanguage
                showNavigation
                rootParent {
                  guid
                  showNavigation
                }
                timePublished
                tags
                tagCategories {
                    name
                    values
                }
                parent {
                    guid
                    url
                }
                ${ownerGroupVcardFragment}
                hasChildren
                children {
                    guid
                    children {
                      guid
                    }
                }
                group {
                    guid
                    ... on Group {
                        name
                        url
                        isClosed
                        introduction
                        canEdit
                        memberCount
                        membership
                        members(limit: 3) {
                            onlineTotal
                            edges {
                                user {
                                    ...UserIntro
                                }
                            }
                        }
                        plugins
                        ...GroupMenuFields
                    }
                }
                rows {
                    ${rowsViewFragment}
                }
                ${accessControlFragment}
            }
        }
    }
    ${pageViewSiteFragment}
    ${userIntroFragment}
    ${groupMenuFieldsFragment}
`

export default Item
